/**
 * hl-stock-config.js
 * Intercepts ATC button click when product is out of stock.
 * - Waitlist mode: opens popup, captures email via Klaviyo
 * - Backorder mode: allows normal add-to-cart
 * - Normal mode: button disabled (handled by Liquid)
 */

(function() {
  'use strict';

  // -- DOM refs --
  let overlay, formState, confirmState, emailInput, errorEl, submitBtn, confirmText;
  let currentProduct = null;
  let currentVariant = null;
  let stockConfig = { mode: 'waitlist' };
  let productData = null;

  // -- Initialise --
  function init() {
    overlay = document.getElementById('hl-waitlist-overlay');
    if (!overlay) return;

    formState = document.getElementById('hl-waitlist-form-state');
    confirmState = document.getElementById('hl-waitlist-confirm');
    emailInput = document.getElementById('hl-waitlist-email');
    errorEl = document.getElementById('hl-waitlist-error');
    submitBtn = document.getElementById('hl-waitlist-submit');
    confirmText = document.getElementById('hl-waitlist-confirm-text');

    // Read stock config
    var configEl = document.querySelector('[data-stock-config]');
    if (configEl) {
      try {
        stockConfig = JSON.parse(configEl.dataset.stockConfig);
      } catch (e) {
        stockConfig = { mode: 'waitlist' };
      }
    }

    // Read product data from script tag (safe from HTML attribute escaping issues)
    var productScript = document.getElementById('hl-product-data');
    if (productScript) {
      try {
        productData = JSON.parse(productScript.textContent);
      } catch (e) {
        productData = null;
      }
    }

    // Waitlist form submit
    var form = document.getElementById('hl-waitlist-form');
    if (form) form.addEventListener('submit', handleWaitlistSubmit);

    // Close handlers
    overlay.querySelector('.hl-waitlist__close').addEventListener('click', closePopup);
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) closePopup();
    });
    document.getElementById('hl-waitlist-continue').addEventListener('click', closePopup);
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closePopup();
    });

    // Block Dawn's section re-render on variant change
    blockDawnRerender();

    // On page load: sync displayed prices + backorder notice to the actual
    // selected variant. The Liquid render now matches the default variant, but
    // this also covers ?variant= deep links and any future render drift.
    var initialVariant = getCurrentVariant();
    if (initialVariant) {
      updateBackorderNotice(initialVariant);
      updatePriceDisplays(initialVariant);
    }

    // Intercept the ATC button click (not the form submit)
    interceptATCButton();

    // Intercept sticky CTA
    interceptStickyCTA();

    // Stock-state line + honest button labels — after interceptStickyCTA so the
    // label updates land on the cloned sticky button, not the discarded one.
    if (initialVariant) updateStockState(initialVariant);
  }

  // -- Get current variant --
  function getCurrentVariant() {
    var mainForm = document.querySelector('product-form form');
    if (!mainForm) return null;
    var variantId = mainForm.querySelector('input[name="id"]').value;
    if (!productData || !productData.variants) return { id: variantId, available: false, title: '', price: 0 };
    var variant = productData.variants.find(function(v) { return v.id == variantId; });
    return variant || { id: variantId, available: false, title: '', price: 0 };
  }

  // -- Should intercept? --
  function shouldOpenWaitlist(variant) {
    // Backorder mode: never intercept (overselling is intended)
    if (stockConfig.mode === 'backorder' && stockConfig.backorder_enabled) return false;
    // Normal mode: Liquid handles via disabled button
    if (stockConfig.mode === 'normal') return false;
    // Waitlist mode (default): intercept if variant is OOS.
    // We check BOTH `available` AND `qty <= 0` because Shopify's CONTINUE
    // inventory policy keeps `variant.available === true` at 0 qty
    // (Liquid quirk), so available alone is not sufficient.
    if (!variant) return true;
    if (variant.available === false) return true;
    if (typeof variant.qty === 'number' && variant.qty <= 0) return true;
    return false;
  }

  // -- Set product/variant context for popup --
  function setContext(variant) {
    var configEl = document.querySelector('[data-stock-config]');
    currentProduct = {
      id: configEl ? configEl.dataset.productId : '',
      title: productData ? productData.title : '',
      handle: configEl ? configEl.dataset.productHandle : '',
      url: configEl ? configEl.dataset.productUrl : window.location.pathname,
      featured_image: productData && productData.featured_image ? productData.featured_image : ''
    };
    var variantTitle = '';
    if (variant.title && variant.title !== 'Default Title') {
      variantTitle = 'Size ' + variant.title;
    }
    currentVariant = {
      id: variant.id,
      title: variantTitle,
      price: variant.price || 0,
      price_formatted: variant.price_formatted || ''
    };
  }

  // -- Intended-spend price: the on-screen ATC price, which is kept in sync with
  //    whatever the shopper has selected — a single variant price (rings) or the
  //    discounted bundle total when a combination is chosen (bundle-selector.js).
  //    This is what we want for "lost revenue" measurement. Falls back to the
  //    selected variant's own price. --
  function hlDisplayedPrice() {
    // Bundle totals only exist as display text (written dot-decimal by
    // bundle-selector's formatter, so parseable). Plain variant prices use the
    // numeric JSON value — presentment money strings like "€234,95" are not
    // safely parseable with a dot-decimal regex.
    if (hlBundleContext().bundle_selected) {
      var el = document.querySelector('[data-product-price]');
      if (el) {
        var n = parseFloat(String(el.textContent).replace(/[^0-9.]/g, ''));
        if (!isNaN(n) && n > 0) return n;
      }
    }
    return currentVariant && currentVariant.price ? (currentVariant.price / 100) : 0;
  }

  // -- Read the currently-selected bundle combination from the DOM (no coupling
  //    to bundle-selector.js internals). Returns { bundle_selected, ... }. --
  function hlBundleContext() {
    var card = document.querySelector('.hl-bundle-selector__card.is-selected');
    var idx = card ? card.getAttribute('data-bundle-index') : null;
    if (!idx || idx === 'none') return { bundle_selected: false };
    var pill = document.querySelector('.hl-bundle-selector__length-btn.is-selected');
    return {
      bundle_selected: true,
      bundle_index: String(idx),
      chain_variant_id: pill ? (pill.getAttribute('data-variant-id') || '') : ''
    };
  }

  // -- Build the shared analytics payload. `value`/`price` is the intended spend
  //    (bundle total when a combination is selected) so summing it across events
  //    estimates lost revenue from out-of-stock demand. --
  function hlEventPayload() {
    var price = hlDisplayedPrice();
    var payload = {
      product_id: currentProduct ? String(currentProduct.id || '') : '',
      variant_id: currentVariant ? String(currentVariant.id || '') : '',
      product_title: currentProduct ? (currentProduct.title || '') : '',
      price: price,
      value: price,
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP'
    };
    var bundle = hlBundleContext();
    payload.bundle_selected = bundle.bundle_selected;
    if (bundle.bundle_selected) {
      payload.bundle_index = bundle.bundle_index;
      payload.chain_variant_id = bundle.chain_variant_id;
    }
    return payload;
  }

  // -- Publish a prefixed custom event for the Web Pixel to forward to GA4/Meta.
  //    Guarded: Shopify.analytics.publish is only present once web-pixels-manager
  //    has initialised; a missing global is a silent no-op (never throws). --
  function hlPublish(name) {
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('heavylies:' + name, hlEventPayload());
      }
    } catch (e) { /* analytics must never break the popup */ }
  }

  // -- Intercept main ATC button click --
  function interceptATCButton() {
    var atcButton = document.querySelector('.hl-add-to-cart');
    if (!atcButton) return;

    // Listen on click at capture phase — fires BEFORE Dawn's submit handler
    atcButton.addEventListener('click', function(e) {
      var variant = getCurrentVariant();
      console.log('[HL] ATC clicked, variant ID:', variant?.id, 'form input:', document.querySelector('product-form form input[name="id"]')?.value);
      if (!shouldOpenWaitlist(variant)) return; // let it through

      // Prevent the click from reaching the form and triggering submit
      e.preventDefault();
      e.stopImmediatePropagation();

      setContext(variant);
      openPopup();
    }, true); // capture phase
  }

  // -- Intercept sticky CTA --
  function interceptStickyCTA() {
    var stickyBtn = document.getElementById('hl-sticky-atc');
    if (!stickyBtn) return;

    // Clone to remove existing handlers
    var newBtn = stickyBtn.cloneNode(true);
    stickyBtn.parentNode.replaceChild(newBtn, stickyBtn);

    newBtn.addEventListener('click', function() {
      var variant = getCurrentVariant();

      if (!shouldOpenWaitlist(variant)) {
        // In stock or backorder — click the main ATC button as before.
        // Only update the label span so the inline-price structure stays intact.
        var mainBtn = document.querySelector('.hl-add-to-cart') ||
                      document.querySelector('product-form button[type="submit"]');
        if (mainBtn && !mainBtn.disabled) {
          var labelEl = newBtn.querySelector('#hl-sticky-atc-label') || newBtn;
          var originalLabel = labelEl.textContent;
          labelEl.textContent = 'Adding...';
          newBtn.disabled = true;
          mainBtn.click();
          setTimeout(function() {
            labelEl.textContent = 'Added \u2713';
            setTimeout(function() {
              labelEl.textContent = originalLabel;
              newBtn.disabled = false;
            }, 1500);
          }, 1000);
        }
        return;
      }

      // Waitlist: open popup
      setContext(variant);
      openPopup();
    });
  }

  // -- Open popup --
  function openPopup() {
    if (!overlay || !currentProduct) return;

    // Populate product info
    document.getElementById('hl-waitlist-title').textContent = currentProduct.title;
    document.getElementById('hl-waitlist-variant').textContent = currentVariant.title || '';
    document.getElementById('hl-waitlist-price').textContent =
      currentVariant.price_formatted ||
      (currentVariant.price ? ('\u00a3' + (currentVariant.price / 100).toFixed(2).replace(/\.00$/, '')) : '');

    var imgEl = document.getElementById('hl-waitlist-image');
    if (currentProduct.featured_image) {
      imgEl.src = currentProduct.featured_image;
      imgEl.alt = currentProduct.title;
    }

    // Reset states
    formState.style.display = '';
    confirmState.classList.remove('is-visible');
    emailInput.value = '';
    errorEl.classList.remove('is-visible');
    submitBtn.disabled = false;
    submitBtn.textContent = 'Notify Me';

    // Show
    overlay.classList.add('is-open');
    // Demand signal — fires on every OOS popup open, even without a submit (Task 3)
    hlPublish('notify_me_click');
    document.body.style.overflow = 'hidden';

    // Focus email input
    setTimeout(function() { emailInput.focus(); }, 100);
  }

  // -- Close popup --
  function closePopup() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';

    // Return focus to ATC button
    var atcBtn = document.querySelector('.hl-add-to-cart');
    if (atcBtn) atcBtn.focus();
  }

  // -- Handle waitlist form submit --
  function handleWaitlistSubmit(e) {
    e.preventDefault();

    var email = emailInput.value.trim();

    // Validate
    if (!email) {
      showError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Please enter a valid email address.');
      return;
    }

    // Disable button
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';
    errorEl.classList.remove('is-visible');

    // Klaviyo identify + track (onsite JS — for analytics + segmentation)
    try {
      var _learnq = window._learnq || [];
      _learnq.push(['identify', { '$email': email }]);
      _learnq.push(['track', 'Waitlist Signup', {
        'ProductName': currentProduct.title,
        'ProductID': String(currentProduct.id || ''),
        'ProductHandle': currentProduct.handle,
        'ProductURL': currentProduct.url,
        'ProductImage': currentProduct.featured_image,
        'VariantTitle': currentVariant.title,
        'VariantID': String(currentVariant.id),
        'Price': currentVariant.price ? (currentVariant.price / 100).toFixed(2) : '0'
      }]);
      window._learnq = _learnq;
    } catch (err) {
      console.error('Klaviyo error:', err);
    }

    // Klaviyo Client API — marketing list subscribe + back-in-stock subscribe.
    // Both fire-and-forget. If either fails we still have the tracked event.
    var KLAVIYO_COMPANY = 'VSCcs6';
    var KLAVIYO_LIST_MARKETING = 'T42YA2'; // Email List
    var KLAVIYO_REVISION = '2024-10-15';
    var KLAVIYO_BASE = 'https://a.klaviyo.com/client';

    // 1) Subscribe to marketing list (list's opt-in setting determines
    //    whether this is instant SUBSCRIBED or pending double-opt-in).
    //    Note: do NOT include `subscriptions` on profile attributes —
    //    Klaviyo rejects it on the Client API; the list relationship
    //    is what drives the subscription + consent.
    fetch(KLAVIYO_BASE + '/subscriptions/?company_id=' + KLAVIYO_COMPANY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Product waitlist',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: KLAVIYO_LIST_MARKETING } }
          }
        }
      })
    }).catch(function(err) { console.error('Klaviyo subscribe error:', err); });

    // 2) Variant-specific back-in-stock subscription (triggers Klaviyo BIS flow on restock)
    if (currentVariant && currentVariant.id) {
      fetch(KLAVIYO_BASE + '/back-in-stock-subscriptions/?company_id=' + KLAVIYO_COMPANY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email }
                }
              },
              channels: ['EMAIL']
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: '$shopify:::$default:::' + String(currentVariant.id)
                }
              }
            }
          }
        })
      }).catch(function(err) { console.error('Klaviyo BIS error:', err); });
    }

    // Publish the signup event for the Web Pixel to forward to GA4 (+ Meta).
    // Direct gtag/fbq calls were removed: those globals don't exist on the
    // storefront — GA4 and Meta run inside sandboxed Shopify Web Pixels.
    hlPublish('back_in_stock_signup');

    // Show confirmation
    formState.style.display = 'none';
    confirmText.textContent = "We'll email you when " + currentProduct.title + " is back, plus drops & launches.";
    confirmState.classList.add('is-visible');
  }

  // -- Show error --
  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.classList.add('is-visible');
    emailInput.focus();
  }

  // -- Block Dawn's section re-render on variant change --
  function blockDawnRerender() {
    if (!productData) return;

    // Monkey-patch the global publish function to block optionValueSelectionChange
    var originalPublish = window.publish || (typeof publish !== 'undefined' ? publish : null);
    if (!originalPublish) return;

    window.publish = function(eventName, data) {
      if (eventName === 'shopify:section:option-value-change' ||
          (typeof PUB_SUB_EVENTS !== 'undefined' && eventName === PUB_SUB_EVENTS.optionValueSelectionChange)) {
        // Handle variant change ourselves — no server re-render
        handleClientSideVariantChange();
        return Promise.resolve();
      }
      return originalPublish.apply(this, arguments);
    };

    // Also patch the global publish if it's different
    if (typeof publish !== 'undefined' && publish !== window.publish) {
      var origPub = publish;
      // Can't reassign const, so intercept at the variant-selects level instead
    }

    // Intercept at the variant-selects element level
    var vs = document.querySelector('variant-selects');
    if (vs) {
      vs.addEventListener('change', function(e) {
        // Stop propagation to prevent Dawn's handler
        e.stopImmediatePropagation();
        handleClientSideVariantChange();
      }, true);
    }
  }

  // -- Sync all price displays to a variant --
  // Uses Liquid-formatted strings from the product JSON so the symbol and
  // format always match the active Markets presentment currency.
  function updatePriceDisplays(variant) {
    if (!variant || !variant.price_formatted) return;

    // ATC button price
    var priceEl = document.querySelector('[data-product-price]');
    if (priceEl) priceEl.textContent = variant.price_formatted;

    // Main price display
    var mainPrice = document.querySelector('.hl-product-price__current');
    if (mainPrice) mainPrice.textContent = variant.price_formatted;

    // Compare-at / SAVE spans (rendered by Liquid, hidden when not on sale)
    var compareEl = document.querySelector('.hl-product-price__compare');
    if (compareEl) {
      if (variant.compare_formatted) {
        compareEl.textContent = variant.compare_formatted;
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }
    var saveEl = document.querySelector('.hl-product-price__save');
    if (saveEl) {
      if (variant.save_formatted) {
        saveEl.textContent = 'SAVE ' + variant.save_formatted;
        saveEl.style.display = '';
      } else {
        saveEl.textContent = '';
        saveEl.style.display = 'none';
      }
    }

    // Klarna split display
    var klarnaEl = document.querySelector('.hl-product-price__payment-terms');
    if (klarnaEl && variant.installment_formatted) {
      klarnaEl.innerHTML = '<span class="hl-klarna-badge">Klarna</span> 3 interest-free payments of ' + variant.installment_formatted;
    }
  }

  function handleClientSideVariantChange() {
    var vs = document.querySelector('variant-selects');
    if (!vs || !productData) return;

    // Get selected option values
    var selectedOptions = [];
    vs.querySelectorAll('input[type="radio"]:checked').forEach(function(r) {
      selectedOptions.push(r.value);
    });

    // Match variant
    var matchedVariant = null;
    if (productData.variants) {
      matchedVariant = productData.variants.find(function(v) {
        return v.title === selectedOptions.join(' / ') || v.title === selectedOptions[0];
      });
    }

    if (matchedVariant) {
      // Update hidden form input
      var idInput = document.querySelector('product-form form input[name="id"]');
      if (idInput) idInput.value = matchedVariant.id;

      // Update URL
      var url = new URL(window.location);
      url.searchParams.set('variant', matchedVariant.id);
      window.history.replaceState({}, '', url);

      // Update all price displays
      updatePriceDisplays(matchedVariant);

      // Show/hide backorder notice based on variant stock
      updateBackorderNotice(matchedVariant);

      // Sync the stock-state line + button labels
      updateStockState(matchedVariant);
    }
  }

  // -- Legacy backorder notice (#hl-backorder-notice) --
  // Superseded by the hl-stock-state line under the price (CRO 2026-06-10):
  // the status line carries the hallmarking/dispatch message, so the old
  // "Pre-order — 3-4 weeks" notice stays permanently hidden (also forced off
  // with !important in hl-stock-state.css so it can never flash on slow loads).
  // Markup kept in hl-product-main.liquid for easy rollback.
  function updateBackorderNotice(variant) {
    var notice = document.getElementById('hl-backorder-notice');
    if (!notice) return;
    notice.style.display = 'none';
  }

  // ================================================================
  // Stock-state line (Option A, under the price) + honest CTA labels
  // Spec: docs/audits/2026-06-10-cro-deep-dive/reports/stock-states-design-options.md
  // ================================================================

  // -- Preview override: ?hl_stock_preview=backorder|waitlist|instock --
  // Forces the DISPLAYED state for verification. Display-only: it never
  // changes shouldOpenWaitlist()/add-to-cart behaviour, so it is harmless
  // if someone uses it on the live storefront.
  function getPreviewState() {
    try {
      var p = new URLSearchParams(window.location.search).get('hl_stock_preview');
      if (p === 'backorder' || p === 'waitlist' || p === 'instock') return p;
    } catch (e) { /* old browsers: no preview */ }
    return null;
  }

  // -- Dispatch cutoff: 2pm Europe/London. Visitor clocks vary, so resolve the
  // hour via Intl/timeZone rather than the local Date hour. hourCycle 'h23'
  // (plus % 24) guards engines that report midnight as "24" under h24.
  // Fallback on ancient browsers: false → keep the server-rendered
  // before-cutoff string.
  function londonDispatchState() {
    // 'today' (working day, before 14:00) | 'next' (after cutoff, dispatch is
    // the next working day) | 'monday' (weekend — say Monday explicitly)
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit', hourCycle: 'h23', weekday: 'short', timeZone: 'Europe/London'
      }).formatToParts(new Date());
      var o = {};
      parts.forEach(function (p) { o[p.type] = p.value; });
      if (o.weekday === 'Sat' || o.weekday === 'Sun') return 'monday';
      if ((parseInt(o.hour, 10) % 24) >= 14) {
        return o.weekday === 'Fri' ? 'monday' : 'next';
      }
      return 'today';
    } catch (e) {
      return 'today'; // keep the server-rendered before-cutoff string
    }
  }

  // -- Resolve display state for a variant (mirrors shouldOpenWaitlist rules) --
  function resolveStockState(variant) {
    var preview = getPreviewState();
    if (preview) return preview;
    var oos = false;
    if (!variant) oos = true;
    else if (variant.available === false) oos = true;
    else if (typeof variant.qty === 'number' && variant.qty <= 0) oos = true;
    if (!oos) return 'instock';
    if (stockConfig.mode === 'backorder' && stockConfig.backorder_enabled) return 'backorder';
    if (stockConfig.mode === 'normal') return 'normal';
    return 'waitlist';
  }

  // -- Paint the status line + CTA labels for the current variant --
  function updateStockState(variant) {
    var state = resolveStockState(variant);
    var root = document.getElementById('hl-stock-state');
    if (root) {
      if (root.getAttribute('data-stock-state') !== state) {
        root.className = 'hl-stock-state hl-stock-state--' + state;
        root.setAttribute('data-stock-state', state);
      }
      var phrase = root.querySelector('.hl-stock-state__phrase');
      var detail = root.querySelector('.hl-stock-state__detail');
      var line2 = root.querySelector('.hl-stock-state__line2');
      var phraseText = '';
      var detailText = '';
      if (state === 'instock') {
        phraseText = 'In stock';
        // Time-aware dispatch copy: "dispatched today if ordered before 2pm"
        // is false after the cutoff — swap to the after-cutoff line.
        var instockDetail = root.dataset.instockDetail || 'dispatched within 1 working day';
        var dispatchState = londonDispatchState();
        if (dispatchState === 'next') {
          instockDetail = root.dataset.instockDetailAfterCutoff || 'dispatched next working day';
        } else if (dispatchState === 'monday') {
          instockDetail = 'dispatched Monday';
        }
        detailText = ' — ' + instockDetail;
      } else if (state === 'backorder') {
        phraseText = root.dataset.backorderMessage || 'In hallmarking at the Birmingham Assay Office — dispatched within 3–4 working days';
      } else if (state === 'waitlist') {
        phraseText = 'Out of stock';
        detailText = ' — join the waitlist';
      } else {
        phraseText = 'Out of stock';
      }
      // Write only on change — the product-info MutationObserver watches this
      // subtree, so unconditional writes would loop.
      if (phrase && phrase.textContent !== phraseText) phrase.textContent = phraseText;
      if (detail && detail.textContent !== detailText) detail.textContent = detailText;
      if (line2) {
        var subText = state === 'backorder' ? (root.dataset.backorderSub || '') : '';
        if (line2.textContent !== subText) line2.textContent = subText;
      }
    }
    updateBuyButtonLabels(state, variant);
  }

  // -- Honest CTA labels: waitlist OOS shows NOTIFY ME WHEN BACK, not ADD TO BAG --
  function updateBuyButtonLabels(state, variant) {
    var wantNotify = state === 'waitlist';

    // Main ATC button. Keep a [data-product-price] span in the DOM either way:
    // updatePriceDisplays(), bundle-selector.js and hlDisplayedPrice() all
    // query it. In notify mode it is simply hidden.
    var atcButton = document.querySelector('.hl-add-to-cart');
    var textEl = atcButton ? atcButton.querySelector('.hl-add-to-cart__text') : null;
    if (textEl) {
      var isNotify = textEl.textContent.indexOf('NOTIFY ME WHEN BACK') !== -1;
      var isSoldOut = textEl.textContent.trim().toUpperCase() === 'SOLD OUT';
      // 'normal' mode: Dawn/Liquid own the disabled SOLD OUT button — leave it.
      if (state !== 'normal' && (wantNotify !== isNotify || isSoldOut)) {
        var priceSpan = textEl.querySelector('[data-product-price]');
        var price = priceSpan ? priceSpan.textContent : '';
        if (!price && variant && variant.price_formatted) price = variant.price_formatted;
        if (wantNotify) {
          textEl.innerHTML = 'NOTIFY ME WHEN BACK<span data-product-price style="display:none;"></span>';
        } else {
          textEl.innerHTML = 'ADD TO BAG — <span data-product-price></span>';
        }
        textEl.querySelector('[data-product-price]').textContent = price;
      }
    }

    // Sticky CTA — label span + inline mobile price span. Only touch the label
    // when it is in a stable state (never stomp "Adding..."/"Added ✓").
    var stickyLabel = document.getElementById('hl-sticky-atc-label');
    if (stickyLabel) {
      var want = wantNotify ? 'Notify Me When Back' : 'Add to Bag';
      var cur = stickyLabel.textContent.trim();
      if (cur !== want && (cur === 'Add to Bag' || cur === 'Notify Me When Back')) {
        stickyLabel.textContent = want;
      }
    }
    var stickyPrice = document.getElementById('hl-sticky-atc-price');
    if (stickyPrice) stickyPrice.style.display = wantNotify ? 'none' : '';
  }

  // -- Go --
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-apply after Dawn's section re-render (variant change triggers server-side re-render)
  var productInfo = document.querySelector('product-info');
  if (productInfo) {
    var observer = new MutationObserver(function() {
      // Dawn replaced the DOM — re-read stock config and fix the button
      var configEl = document.querySelector('[data-stock-config]');
      if (!configEl) return;

      try {
        stockConfig = JSON.parse(configEl.dataset.stockConfig);
      } catch (e) {
        stockConfig = { mode: 'waitlist' };
      }

      // Re-read product data
      var productScript = document.getElementById('hl-product-data');
      if (productScript) {
        try { productData = JSON.parse(productScript.textContent); } catch (e) {}
      }

      // Fix ATC button — Dawn may have set it to disabled/SOLD OUT.
      // updateStockState() rewrites the label honestly per state (waitlist →
      // NOTIFY ME WHEN BACK, otherwise ADD TO BAG — price) and refreshes the
      // status line. It only writes on change, so it cannot loop the observer.
      var atcButton = document.querySelector('.hl-add-to-cart');
      if (atcButton && stockConfig.mode !== 'normal') {
        atcButton.disabled = false;
      }
      updateStockState(getCurrentVariant());

      // Re-intercept the new ATC button
      interceptATCButton();
    });

    observer.observe(productInfo, { childList: true, subtree: true });
  }

  window.__hlStockConfigLoaded = true;
})();
